import { useMutation, useQuery } from '@apollo/client/react'
import { CertificateOrientation } from '@zeus/index'
import { Eye, FileText, Loader, Upload, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_CERTIFICATE_TEMPLATE, GQL_UPDATE_CERTIFICATE_TEMPLATE } from '~/gql/certificates/certificates'
import { env } from '~/helpers/env'

/** Taille max du logo uploadé en data-URI (base64 stocké en DB), comme le branding. */
const MAX_LOGO_BYTES = 512 * 1024

/** Taille max du squelette PDF (aligné sur la limite serveur). */
const MAX_SKELETON_BYTES = 3 * 1024 * 1024

/** Placeholders injectables dans le corps du modèle, affichés en aide. */
const PLACEHOLDERS = [
  '{{firstName}}',
  '{{lastName}}',
  '{{moduleTitle}}',
  '{{sessionName}}',
  '{{completedAt}}',
  '{{score}}',
  '{{totalTime}}',
  '{{appName}}',
]

/** Lit un fichier image en data-URI base64. */
function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

type FormState = {
  title: string
  body: string
  signature: string
  showScore: boolean
  showDate: boolean
  showTotalTime: boolean
  orientation: CertificateOrientation
  logo: string
  marginTop: number
  marginBottom: number
  marginLeft: number
  marginRight: number
}

const EMPTY_FORM: FormState = {
  title: '',
  body: '',
  signature: '',
  showScore: true,
  showDate: true,
  showTotalTime: false,
  orientation: CertificateOrientation.LANDSCAPE,
  logo: '',
  marginTop: 64,
  marginBottom: 64,
  marginLeft: 60,
  marginRight: 60,
}

function CertificateSettings() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_CERTIFICATE_TEMPLATE)
  const [updateTemplate, { loading: saving }] = useMutation(GQL_UPDATE_CERTIFICATE_TEMPLATE)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [previewing, setPreviewing] = useState(false)
  // Squelette PDF : undefined = inchangé (le PDF stocké n'est pas rechargé), '' =
  // retrait demandé, data-URI = nouveau fichier. `hasStoredSkeleton` vient de la query.
  const [skeleton, setSkeleton] = useState<string | undefined>(undefined)
  const hasStoredSkeleton = data?.certificateTemplate?.hasSkeleton ?? false
  const skeletonActive = skeleton === undefined ? hasStoredSkeleton : skeleton !== ''

  useEffect(() => {
    const tpl = data?.certificateTemplate
    if (!tpl) return
    setSkeleton(undefined)
    setForm({
      title: tpl.title ?? '',
      body: tpl.body ?? '',
      signature: tpl.signature ?? '',
      showScore: tpl.showScore ?? true,
      showDate: tpl.showDate ?? true,
      showTotalTime: tpl.showTotalTime ?? false,
      orientation: (tpl.orientation as CertificateOrientation) ?? CertificateOrientation.LANDSCAPE,
      logo: tpl.logo ?? '',
      marginTop: tpl.marginTop ?? 64,
      marginBottom: tpl.marginBottom ?? 64,
      marginLeft: tpl.marginLeft ?? 60,
      marginRight: tpl.marginRight ?? 60,
    })
  }, [data])

  const set = <K extends keyof FormState>(key: K) => (value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  const toInput = () => ({
    title: form.title || undefined,
    body: form.body || undefined,
    // Chaîne vide envoyée telle quelle : elle efface la signature côté API.
    signature: form.signature,
    showScore: form.showScore,
    showDate: form.showDate,
    showTotalTime: form.showTotalTime,
    orientation: form.orientation,
    // Chaîne vide = effacement → repli sur le logo d'instance côté API.
    logo: form.logo,
    marginTop: form.marginTop,
    marginBottom: form.marginBottom,
    marginLeft: form.marginLeft,
    marginRight: form.marginRight,
    // N'envoyer le squelette que s'il a changé (le PDF stocké, jusqu'à 3 Mo,
    // n'est pas rechargé). undefined → l'API laisse le squelette inchangé.
    ...(skeleton !== undefined ? { skeletonPdf: skeleton } : {}),
  })

  const handleSkeletonChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.type !== 'application/pdf') {
      toast.error(t('certificates.skeleton.typeError'))
      return
    }
    if (file.size > MAX_SKELETON_BYTES) {
      toast.error(t('certificates.skeleton.sizeError'))
      return
    }
    try {
      setSkeleton(await readFileAsDataUri(file))
    } catch {
      toast.error(t('certificates.skeleton.readError'))
    }
  }

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // autorise le re-choix du même fichier
    if (!file) return
    if (!/^image\/(?:png|jpeg)$/.test(file.type)) {
      toast.error(t('certificates.logo.typeError'))
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(t('certificates.logo.sizeError'))
      return
    }
    try {
      set('logo')(await readFileAsDataUri(file))
    } catch {
      toast.error(t('certificates.logo.readError'))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateTemplate({ variables: { input: toInput() } })
      await refetch()
      toast.success(t('certificates.saved'))
    } catch {
      toast.error(t('certificates.saveError'))
    }
  }

  /** Prévisualise le formulaire courant (non sauvegardé) dans un nouvel onglet. */
  const handlePreview = async () => {
    setPreviewing(true)
    try {
      const res = await fetch(`${env.api.url}/certificates/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(toInput()),
      })
      if (!res.ok) throw new Error(`preview failed (${res.status})`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener')
      // Laisse le temps au nouvel onglet de charger le blob avant révocation.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      toast.error(t('certificates.previewError'))
    } finally {
      setPreviewing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Loader className="size-6 animate-spin" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('certificates.template.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certTitle">{t('certificates.field.title')}</Label>
            <Input
              id="certTitle"
              value={form.title}
              onChange={e => set('title')(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certBody">{t('certificates.field.body')}</Label>
            <textarea
              id="certBody"
              value={form.body}
              onChange={e => set('body')(e.target.value)}
              rows={5}
              className="rounded-md border border-border bg-transparent px-3 py-2 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t('certificates.placeholders.help')}
              {' '}
              {PLACEHOLDERS.join(' ')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certSignature">{t('certificates.field.signature')}</Label>
            <Input
              id="certSignature"
              value={form.signature}
              onChange={e => set('signature')(e.target.value)}
              placeholder={t('certificates.field.signaturePlaceholder')}
            />
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.showScore}
                onChange={e => set('showScore')(e.target.checked)}
              />
              {t('certificates.field.showScore')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.showDate}
                onChange={e => set('showDate')(e.target.checked)}
              />
              {t('certificates.field.showDate')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.showTotalTime}
                onChange={e => set('showTotalTime')(e.target.checked)}
              />
              {t('certificates.field.showTotalTime')}
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certOrientation">{t('certificates.field.orientation')}</Label>
            <select
              id="certOrientation"
              value={form.orientation}
              onChange={e => set('orientation')(e.target.value as CertificateOrientation)}
              className="h-9 w-full rounded-md border border-border bg-transparent px-3 text-sm"
            >
              <option value="LANDSCAPE">{t('certificates.orientation.landscape')}</option>
              <option value="PORTRAIT">{t('certificates.orientation.portrait')}</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('certificates.field.logo')}</Label>
            <p className="text-xs text-muted-foreground">{t('certificates.logo.help')}</p>
            <div className="flex items-center gap-3">
              {form.logo
                ? <img src={form.logo} alt="" className="h-12 w-auto max-w-[160px] rounded border border-border object-contain" />
                : <span className="text-xs text-muted-foreground">{t('certificates.logo.fallback')}</span>}
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">
                <Upload className="size-4" />
                {t('certificates.logo.upload')}
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleLogoChange} />
              </label>
              {form.logo && (
                <Button type="button" variant="ghost" size="sm" onClick={() => set('logo')('')}>
                  <X className="mr-1 size-4" />
                  {t('certificates.logo.remove')}
                </Button>
              )}
            </div>
          </div>

          {/* Squelette PDF de fond */}
          <div className="flex flex-col gap-1.5 border-t border-border pt-4">
            <Label>{t('certificates.field.skeleton')}</Label>
            <p className="text-xs text-muted-foreground">{t('certificates.skeleton.help')}</p>
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-sm">
                <FileText className="size-4 text-muted-foreground" />
                {skeletonActive ? t('certificates.skeleton.active') : t('certificates.skeleton.none')}
              </span>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">
                <Upload className="size-4" />
                {skeletonActive ? t('certificates.skeleton.replace') : t('certificates.skeleton.upload')}
                <input type="file" accept="application/pdf" className="hidden" onChange={handleSkeletonChange} />
              </label>
              {skeletonActive && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setSkeleton('')}>
                  <X className="mr-1 size-4" />
                  {t('certificates.skeleton.remove')}
                </Button>
              )}
            </div>
          </div>

          {/* Marges du bloc de texte (utiles surtout avec un squelette) */}
          <div className="flex flex-col gap-1.5">
            <Label>{t('certificates.field.margins')}</Label>
            <p className="text-xs text-muted-foreground">{t('certificates.margins.help')}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(['marginTop', 'marginBottom', 'marginLeft', 'marginRight'] as const).map(key => (
                <div key={key} className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">{t(`certificates.margins.${key}`)}</span>
                  <Input
                    type="number"
                    min={0}
                    max={300}
                    value={form[key]}
                    onChange={e => set(key)(Number(e.target.value))}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={handlePreview} disabled={previewing}>
          {previewing
            ? <Loader className="mr-2 size-4 animate-spin" />
            : <Eye className="mr-2 size-4" />}
          {t('certificates.preview')}
        </Button>
        <Button type="submit" disabled={saving}>
          {saving && <Loader className="mr-2 size-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export default CertificateSettings
